const cities = [
  { name: "Aventura", zips: "33180" },
  { name: "Sunny Isles Beach", zips: "33160" },
  { name: "North Miami Beach", zips: "33162" },
  { name: "Hallandale Beach", zips: "33009" },
  { name: "Hollywood", zips: "33019" },
  { name: "Golden Beach", zips: "33160" },
  { name: "Bal Harbour", zips: "33154" },
  { name: "Surfside", zips: "33154" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your Aventura condo or office in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function AventuraLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Aventura &amp; Greater Miami-Fort Lauderdale
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in a luxury high-rise on Williams Island, a waterfront condo in Sunny Isles, or anywhere between Miami and Fort Lauderdale, Fast Passport Center makes expedited passport processing simple — no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Aventura is one of the youngest and most dynamic cities in South Florida, incorporated in 1995 and built from the ground up as a master-planned luxury community. What began as wetlands and mangroves between Miami and Fort Lauderdale has become one of the most affluent, internationally diverse, and travel-oriented cities in the United States. The city&apos;s very name — Spanish for &ldquo;adventure&rdquo; — reflects the spirit of its residents, who treat international travel not as a luxury but as a way of life.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Aventura Mall anchors the city&apos;s identity as a world-class shopping and lifestyle destination. Consistently ranked among the top five highest-grossing malls in the United States, Aventura Mall draws visitors from across Latin America, the Caribbean, and Europe who come specifically to shop brands that are unavailable or heavily taxed in their home countries. Nordstrom, Bloomingdale&apos;s, Apple, and dozens of luxury boutiques line a shopping experience that is as much a destination as any resort. For Aventura residents, the mall is simply the neighborhood — a place where you run into friends from Caracas, Bogota, or Sao Paulo while buying a coffee.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city&apos;s skyline is defined by luxury high-rise condominiums that rival anything in Miami Beach or Brickell. Williams Island — a private enclave with its own golf course, marina, and country club — sits at the eastern edge. The Peninsula, Hidden Bay, Porto Vita, and Turnberry Isle towers rise along the Intracoastal Waterway, offering residents direct yacht access, private docks, and panoramic views of the Atlantic. These are not vacation homes for most residents — they are primary residences for families who split time between South Florida and cities across Latin America, Europe, and beyond.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Aventura&apos;s demographic profile is unique even within South Florida&apos;s already diverse landscape. The city has one of the highest concentrations of Venezuelan, Colombian, Brazilian, and Argentine residents of any U.S. city. Spanish and Portuguese are heard as frequently as English in grocery stores, restaurants, and business meetings. The Promenade at Aventura and the Aventura Arts &amp; Cultural Center reflect this international character, hosting events, exhibitions, and performances that draw on traditions from across the Americas. This is a community where a teenager might spend summers with grandparents in Caracas, where a business executive commutes quarterly to Sao Paulo, and where a family plans Christmas in Buenos Aires as routinely as others plan trips to Orlando.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city&apos;s location is arguably the best in South Florida for international travel. Fort Lauderdale-Hollywood International Airport (FLL) is approximately 15 minutes south via I-95. Miami International Airport (MIA) — the busiest U.S. airport for international passengers — is roughly 25 minutes south. Port Everglades, one of the world&apos;s busiest cruise ports, is about 20 minutes away. The Port of Miami is roughly 25 minutes south. No other residential community in South Florida sits so precisely at the intersection of every major international departure point. For Aventura residents, choosing between MIA and FLL is a matter of which airline has the better direct flight — not a question of convenience.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Aventura&apos;s business community is equally international. The city hosts regional headquarters for Latin American financial services firms, import-export companies, real estate developers, and technology consultancies. Professionals here do not occasionally travel abroad — they conduct business across borders as a core function of their careers. A real estate developer might have projects in Panama, Medellin, and Miami simultaneously. A wealth manager might serve clients in six countries. A logistics executive might need to be in Mexico City next Tuesday for a supply chain negotiation.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            What all of these patterns create is a city where passport issues are not abstract inconveniences — they are immediate, high-stakes crises. A Venezuelan-American executive at Aventura Mall discovers her passport expires the same week she is scheduled to close a deal in Caracas. A Brazilian family at Turnberry Isle realizes their daughter&apos;s five-year passport expired two months before their annual Christmas trip to Rio. A Colombian business owner at Williams Island needs an emergency renewal for a Medellin conference he booked three weeks ago. Fast Passport Center has processed expedited passports for Aventura travelers across every one of these scenarios. We understand that in this city, international travel is not an occasional event — it is the infrastructure of daily life. Our courier-based expedited service means you start online from your condo, your office at the Promenade, or your yacht club — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Aventura — approximately 40 miles north on I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Miami-Dade and Broward Counties</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Miami-Dade and Broward Counties — start online, ship overnight, and we handle the rest. No need to drive to a passport agency.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Miami", href: "/miami" },
                  { name: "Fort Lauderdale", href: "/fort-lauderdale" },
                  { name: "Coral Gables", href: "/coral-gables" },
                  { name: "Weston", href: "/weston" },
                  { name: "Boca Raton", href: "/boca-raton" },
                  { name: "Palm Beach", href: "/palm-beach" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}